import React, { useEffect, useState } from 'react'

import type { EventTracker } from '../analytics/event-tracker'
import { LoggingEventTracker } from '../analytics/logging-event-tracker'
import { MixpanelEventTracker } from '../analytics/mixpanel-event-tracker'
import { useLogger } from '../logger'
import { newMixpanel, shouldUseMixpanel } from '../utils/mixpanel'

const createLoggingTracker = () => new LoggingEventTracker()

interface EventTrackerContextType {
  tracker: EventTracker
  isReady: boolean
}

const EventTrackerContext = React.createContext<EventTrackerContextType>({
  tracker: createLoggingTracker(),
  isReady: false,
})

export function useEventTracker() {
  const context = React.useContext(EventTrackerContext)
  if (!context) {
    throw new Error(`useEventTracker hook must be used within EventTrackerProvider`)
  }
  return context
}

export function EventTrackerProvider({ children }: { children: React.ReactNode }) {
  const logger = useLogger()
  const [tracker, setTracker] = useState<EventTracker>(createLoggingTracker())
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    // Only run on client
    if (typeof window === 'undefined') {
      return
    }

    if (!shouldUseMixpanel()) {
      setTracker(createLoggingTracker())
      setIsReady(true)
      return
    }

    let cancelled = false
    newMixpanel()
      .then(mp => {
        if (cancelled) {
          return
        }
        setTracker(new MixpanelEventTracker(mp))
        setIsReady(true)
      })
      .catch(e => {
        logger.error('Mixpanel failed to initialize, falling back to LoggingEventTracker', e)
        setTracker(createLoggingTracker())
        setIsReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [logger])

  return <EventTrackerContext.Provider value={{ tracker, isReady }}>{children}</EventTrackerContext.Provider>
}
